import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { getSubscriptionNotificationData, getSubscriptionNotificationLastFetchedAt, getPrevUrl } from '@/selectors';
import { fetchSubscriptionNotifications } from '@/actions';
import { useNotification } from '@/context/NotificationContext';
import { ROUTES } from '@/config/routes';
import ArrowRight from '@/images/icons/ArrowRight';
import { isActiveSubscription } from '@/utils/utils';

const SubscriptionNotifications = () => {
  const dispatch = useDispatch();
  const { showNotification, hideNotification } = useNotification();
  const subscriptionData = useSelector(getSubscriptionNotificationData);
  const lastFetchedAt = useSelector(getSubscriptionNotificationLastFetchedAt);
  const auth = useSelector(state => state.auth);
  const location = useLocation();
  const prevUrl = useSelector(getPrevUrl);
  
  const initialFetchDone = useRef(false);
  const currentNotificationType = useRef(null);
  
  const {
    subscription,
    salesLimit,
    salesVolume,
    invoices = [],
  } = subscriptionData;

  const isSubscriptionPage = useMemo(() => {
    const path = location.pathname;
    return path.startsWith('/manage-subscription');
  }, [location.pathname]);
  
  useEffect(() => {
    if (prevUrl && location.pathname !== prevUrl) {
      initialFetchDone.current = false;
    }
  }, [location.pathname, prevUrl]);
  
  useEffect(() => {
    if (!auth?.user?.token) return;
    
    // skip fetching on subscription page
    if (isSubscriptionPage) {
      return;
    }
    
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      
      // if we need to refresh based on lastFetchedAt
      let shouldFetch = true;

      if (lastFetchedAt) {
        const lastFetchDate = new Date(lastFetchedAt);
        const currentDate = new Date();
        const oneDayInMs = 24 * 60 * 60 * 1000;
        
        shouldFetch = currentDate - lastFetchDate > oneDayInMs;
      }
      
      if (shouldFetch) {
        dispatch(fetchSubscriptionNotifications(auth.user.token));
      }
    }
  }, [auth, location.pathname, isSubscriptionPage]);
  
  const getUpgradePlanLink = useCallback(() => (
    <Link to={ROUTES.getSubscriptionRoutes().manageSubscription_changePlan} className="inline md:inline-block md:min-w-[initial] text-white md:text-brand-green md:text-sm/[1.3] md:py-1.5 md:px-3 md:bg-white md:hover:bg-[#EAE6E0] md:rounded-full transition md:ml-auto ml-0.5">
      <span className="border-b border-b-white md:border-none">Upgrade my plan</span>
      <ArrowRight className="inline md:hidden w-4 h-3" />
    </Link>
  ), []);
  
  const getPayLink = useCallback(() => {
    if (!invoices || invoices.length === 0) return null;
    
    return (
      <Link to={invoices[0].hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="inline md:inline-block md:min-w-[initial] text-white md:text-brand-green md:text-sm/[1.3] md:py-1.5 md:px-3 md:bg-white md:hover:bg-[#EAE6E0] md:rounded-full transition md:ml-auto ml-0.5">
        <span className="border-b border-b-white md:border-none">Pay</span>
        <ArrowRight className="inline md:hidden w-4 h-3" />
      </Link>
    );
  }, [invoices]);

  const salesVolumePercentage = useMemo(() => {
    if (!salesLimit || !salesVolume) return 0;
    if (salesLimit === -1) return -1;

    return Math.min(Math.round((Math.round(salesVolume) / salesLimit) * 100), 100);
  }, [salesLimit, salesVolume]);
  
  useEffect(() => {
    let notificationType = null;
    let message = '';
    let type = 'info';
    let action = null;
  
    if (!subscription) {
      hideNotification();
      return;
    }
    
    if (isActiveSubscription(subscription) && salesVolumePercentage >= 100) {
      notificationType = 'limits_exceeded';
      message = 'Plan limits have been exceeded, new payments are no longer being accepted.';
      type = 'warning';
      action = getUpgradePlanLink();
    } else if (isActiveSubscription(subscription) && salesVolumePercentage >= 80) {
      notificationType = 'limits_near';
      message = 'Plan limits will soon be exceeded (80% reached).';
      type = 'info';
      action = getUpgradePlanLink();
    } else if (subscription?.stripeSubscription?.status === 'past_due') {
      notificationType = 'past_due';
      message = 'Your last payment was unsuccessful. Please update your billing info!';
      type = 'warning';
    } else if (subscription?.stripeSubscription?.status === 'unpaid') {
      notificationType = 'unpaid';
      message = 'Your subscription was canceled automatically due to unpaid invoices.';
      type = 'warning';
    }

    if (invoices && invoices.length > 0) {
      action = getPayLink();
    }

    if (notificationType !== currentNotificationType.current) {
      currentNotificationType.current = notificationType;
      
      if (notificationType) {
        showNotification(message, type, action);
      } else {
        hideNotification();
      }
    }
  }, [
    auth,
    salesVolume,
    invoices,
    showNotification,
    hideNotification,
    getUpgradePlanLink,
    getPayLink,
    salesVolumePercentage,
    subscription,
    location.pathname,
    isSubscriptionPage
  ]);
  
  // The component doesn't render UI, it only manages notifications
  return null;
};

export default SubscriptionNotifications; 